/** Shared defaults for report profile team vs company bases. */

/**
 * Classic 5X / PE-portfolio roles.
 * Kept for backward compatibility with existing 5X workspace profile pickers.
 */
export const DEFAULT_PROFILE_ROLES_5X = ["CEO", "Leadership Team", "PE Sponsor", "Board Member", ""];

/**
 * Neutral roles for generic (non-5X) workspaces.
 * No PE-specific titles.
 */
export const DEFAULT_PROFILE_ROLES_GENERIC = ["Executive", "Leadership Team", "Manager", "Contributor", ""];

/**
 * Legacy export — resolves to 5X roles.
 * New code should use {@link getRolesForFormat} instead.
 * @deprecated use getRolesForFormat(assessmentFormat)
 */
export const PROFILE_ROLES = DEFAULT_PROFILE_ROLES_5X;

/**
 * Return the appropriate role list for the given assessment format.
 * Returns generic roles for custom (non-5X) formats; classic 5X roles otherwise.
 * @param {object|null} assessmentFormat
 * @returns {string[]}
 */
export function getRolesForFormat(assessmentFormat) {
  if (!isFiveXAssessmentFormat(assessmentFormat)) return DEFAULT_PROFILE_ROLES_GENERIC;
  return DEFAULT_PROFILE_ROLES_5X;
}

export const DEFAULT_COMPANY_PROFILE_ID = "company_analyzed";
export const DEFAULT_TEAM_PROFILE_ID = "team_analyzed";

/** System profiles that still use classic statement-table blocks (never generate those). */
export const CLASSIC_COMPANY_PROFILE_IDS = new Set([
  "company_full",
  "ceo_full",
  "leadership_team",
  "pe_sponsor",
]);
export const CLASSIC_TEAM_PROFILE_IDS = new Set(["team_full", "team_leadership"]);

/** Display label for built-in company report formats in Pipeline dropdowns. */
export function formatCompanyProfileLabel(profile) {
  const id = profile?.profile_id || "";
  const name = profile?.name || "Company Report";
  if (id === "company_analyzed") return `${name} (AI Professional)`;
  return name;
}

/** Display label for built-in team report formats in Pipeline dropdowns. */
export function formatTeamProfileLabel(profile) {
  const id = profile?.profile_id || "";
  const name = profile?.name || "Team Report";
  if (id === "team_analyzed") return `${name} (AI Professional)`;
  return name;
}

/**
 * Resolve which company profile to generate. Classic table packs always route
 * to the AI professional analyzed report.
 */
export function resolveCompanyProfileId(profileId) {
  if (!profileId || CLASSIC_COMPANY_PROFILE_IDS.has(profileId)) return "company_analyzed";
  return profileId;
}

/**
 * Resolve which team profile to generate. Classic table packs always route
 * to the AI professional analyzed report.
 */
export function resolveTeamProfileId(profileId) {
  if (!profileId || CLASSIC_TEAM_PROFILE_IDS.has(profileId)) return "team_analyzed";
  return profileId;
}

/** Resolve either company or team classic profile IDs to analyzed packs. */
export function resolveModularProfileId(profileId) {
  return resolveTeamProfileId(resolveCompanyProfileId(profileId));
}

export function isTeamReportBase(reportBase) {
  return reportBase === "team";
}

export function filterCatalogForBase(catalog, reportBase) {
  const isTeamBase = isTeamReportBase(reportBase);
  return (catalog || []).filter((b) => {
    if (isTeamBase) return b.block_id !== "individual_breakdown" && !b.company_base_only;
    return !b.team_base_only;
  });
}

/** Classic 5X system formats (hide 5X-only blocks when false). */
export function isFiveXAssessmentFormat(format) {
  if (!format) return true;
  const key = String(format.key || format.type_key || "").trim().toLowerCase();
  if (!key) return Boolean(format.is_system);
  return (
    key === "5x_v1" ||
    key === "5x_team_assessment" ||
    key === "5x" ||
    key === "5x_team"
  );
}

/**
 * Filter catalog for report base + assessment format.
 * Hides blocks marked five_x_only when the selected format is not classic 5X.
 */
export function filterCatalogForProfile(catalog, reportBase, assessmentFormat) {
  const baseFiltered = filterCatalogForBase(catalog, reportBase);
  if (isFiveXAssessmentFormat(assessmentFormat)) return baseFiltered;
  return baseFiltered.filter((b) => !b.five_x_only);
}

/** Section labels for the report scope panel. */
export function assessmentFormatSectionLabels(format) {
  const sections = format?.sections || [];
  return sections
    .map((s) => (typeof s === "string" ? s : s?.label || s?.key || ""))
    .map((s) => String(s || "").trim())
    .filter(Boolean);
}

/** New profiles default to the five independent analyzed blocks (classic 5X). */
export function defaultAnalyzedSplitBlocks() {
  return [
    { block_id: "analyzed_rag", order: 1, enabled: true },
    { block_id: "analyzed_gemini", order: 2, enabled: true },
    { block_id: "analyzed_executive_summary", order: 3, enabled: true },
    { block_id: "analyzed_pillar_charts", order: 4, enabled: true },
    { block_id: "analyzed_per_pillar", order: 5, enabled: true },
  ];
}

/**
 * Default blocks for a generic (non-5X) company profile.
 * Gemini narrative + assessment section scores — no classic 5X pillar pages.
 */
export function defaultGenericBlocks() {
  return [
    { block_id: "analyzed_gemini",            order: 1, enabled: true },
    { block_id: "analyzed_executive_summary", order: 2, enabled: true },
    { block_id: "section_scores",             order: 3, enabled: true },
    { block_id: "top_priorities",             order: 4, enabled: true },
    { block_id: "opportunity_gaps",           order: 5, enabled: true },
  ];
}

/**
 * Return starter blocks for a new profile.
 *
 * @param {string} reportBase          - "company" or "team"
 * @param {object|null} assessmentFormat - the selected assessment-type object (or null)
 *   When null / 5X format → classic 5X blocks.
 *   When a custom (non-5X) format → generic blocks (section_scores-based).
 *   Callers that don't know the format yet can omit the second arg safely;
 *   they will receive the classic blocks (unchanged backward-compat behaviour).
 */
export function defaultBlocksForBase(reportBase, assessmentFormat = null) {
  if (isTeamReportBase(reportBase)) {
    return [{ block_id: "analyzed_team", order: 1, enabled: true }];
  }
  if (!isFiveXAssessmentFormat(assessmentFormat)) return defaultGenericBlocks();
  return defaultAnalyzedSplitBlocks();
}

/** Starting points when creating a new company-base profile (classic 5X). */
export const COMPANY_PROFILE_PRESETS = [
  {
    id: "company_analyzed",
    label: "Company Analyzed (AI Professional)",
    description:
      "RAG + Gemini briefing from independent blocks: RAG, Gemini, Executive Summary, Exact Pillar Charts, Per-Pillar Analytics.",
    blocks: defaultAnalyzedSplitBlocks(),
  },
];

/**
 * Starting points when creating a profile for a custom (non-5X) assessment format.
 * These presets use format-agnostic blocks that render whatever sections the
 * tenant has defined — no 5X pillar charts or framework intro pages.
 */
export const COMPANY_PROFILE_PRESETS_GENERIC = [
  {
    id: "generic_section_scores",
    label: "Gemini + Section Scores",
    description:
      "AI briefing (Gemini), Executive Summary, then Section Scores / priorities / gaps driven by your assessment format — no classic 5X pillars.",
    blocks: defaultGenericBlocks(),
  },
];

/**
 * Return the right preset list for the given assessment format.
 * @param {object|null} assessmentFormat
 */
export function getPresetsForFormat(assessmentFormat) {
  if (!isFiveXAssessmentFormat(assessmentFormat)) return COMPANY_PROFILE_PRESETS_GENERIC;
  return COMPANY_PROFILE_PRESETS;
}

/**
 * Expand legacy analyzed_company into the five split blocks (idempotent).
 * Migrated recipes keep full_analyzed_pack so PDF matches the old composite.
 */
export function migrateLegacyAnalyzedCompanyBlocks(blocks) {
  const raw = (blocks || []).filter((b) => b && typeof b === "object");
  const typeOf = (b) => String(b.block_type || b.block_id || "").trim();
  const hasEnabledLegacy = raw.some(
    (b) => typeOf(b) === "analyzed_company" && b.enabled !== false
  );
  const withoutLegacy = raw.filter((b) => typeOf(b) !== "analyzed_company");
  if (!hasEnabledLegacy) return withoutLegacy;

  const splitOrder = [
    "analyzed_rag",
    "analyzed_gemini",
    "analyzed_executive_summary",
    "analyzed_pillar_charts",
    "analyzed_per_pillar",
  ];
  let insertAt = 1;
  for (const b of raw) {
    if (typeOf(b) === "analyzed_company" && b.enabled !== false) {
      insertAt = Number(b.order) || 1;
      break;
    }
  }
  const present = new Set(withoutLegacy.map(typeOf));
  const added = [];
  let orderCursor = insertAt;
  for (const bid of splitOrder) {
    if (present.has(bid)) continue;
    added.push({
      block_id: bid,
      block_type: bid,
      order: orderCursor,
      enabled: true,
      config: { full_analyzed_pack: true },
    });
    orderCursor += 1;
    present.add(bid);
  }
  const out = withoutLegacy.map((b) => {
    const bid = typeOf(b);
    if (!splitOrder.includes(bid)) return b;
    return {
      ...b,
      block_id: bid,
      block_type: bid,
      config: { ...(b.config || {}), full_analyzed_pack: true },
    };
  });
  out.push(...added);
  out.sort((a, b) => (Number(a.order) || 99) - (Number(b.order) || 99));
  return out;
}

/** System analyzed packs — hide these in generic / custom-format workspaces. */
export const FIVE_X_SYSTEM_ANALYZED_IDS = new Set(["company_analyzed", "team_analyzed"]);

/** Hide classic table-report system profiles from Pipeline / Studio pickers. */
export function isSelectableReportProfile(profile) {
  const id = profile?.profile_id || "";
  if (CLASSIC_COMPANY_PROFILE_IDS.has(id) || CLASSIC_TEAM_PROFILE_IDS.has(id)) return false;
  return true;
}

/**
 * Profiles shown in Report Profiles / Pipeline dropdowns.
 * When the workspace has any custom (non-system) profile, hide the classic
 * company_analyzed / team_analyzed packs so FitCheck-style tenants aren't
 * stuck picking 5X pillar reports.
 */
export function filterProfilesForWorkspace(profiles, { preferCustom = true } = {}) {
  const list = (profiles || []).filter(isSelectableReportProfile);
  const hasCustom = list.some((p) => !p.is_system);
  let out = list;
  if (preferCustom && hasCustom) {
    out = list.filter(
      (p) => !(p.is_system && FIVE_X_SYSTEM_ANALYZED_IDS.has(p.profile_id || ""))
    );
  }
  // Custom first, then remaining system
  return [...out].sort((a, b) => {
    const as = a.is_system ? 1 : 0;
    const bs = b.is_system ? 1 : 0;
    if (as !== bs) return as - bs;
    return String(a.name || "").localeCompare(String(b.name || ""));
  });
}

/** Default company profile id for a workspace profile list. */
export function defaultCompanyProfileIdFromList(profiles) {
  const company = filterProfilesForWorkspace(profiles).filter((p) => p.is_team_base !== true);
  const custom = company.find((p) => !p.is_system);
  if (custom?.profile_id) return custom.profile_id;
  return DEFAULT_COMPANY_PROFILE_ID;
}

/** Default team profile id for a workspace profile list. */
export function defaultTeamProfileIdFromList(profiles) {
  const team = filterProfilesForWorkspace(profiles).filter((p) => p.is_team_base === true);
  const custom = team.find((p) => !p.is_system);
  if (custom?.profile_id) return custom.profile_id;
  return DEFAULT_TEAM_PROFILE_ID;
}

/** Prefer a saved id only if it still appears in the workspace picker. */
export function coerceCompanyProfileId(profileId, profiles) {
  const resolved = resolveCompanyProfileId(profileId);
  const company = filterProfilesForWorkspace(profiles).filter((p) => p.is_team_base !== true);
  if (company.some((p) => p.profile_id === resolved)) return resolved;
  return defaultCompanyProfileIdFromList(profiles);
}

/** Prefer a saved team id only if it still appears in the workspace picker. */
export function coerceTeamProfileId(profileId, profiles) {
  const resolved = resolveTeamProfileId(profileId);
  const team = filterProfilesForWorkspace(profiles).filter((p) => p.is_team_base === true);
  if (team.some((p) => p.profile_id === resolved)) return resolved;
  return defaultTeamProfileIdFromList(profiles);
}

/** Blocks that may appear more than once in a profile recipe. */
export const REPEATABLE_BLOCK_TYPES = new Set([
  "static_text",
  "static_heading",
  "page_break",
]);

export function isRepeatableBlockType(blockType) {
  return REPEATABLE_BLOCK_TYPES.has(String(blockType || "").trim());
}

export function defaultConfigForBlockType(blockType) {
  switch (String(blockType || "").trim()) {
    case "static_text":
      return { title: "", html: "" };
    case "static_heading":
      return { title: "", subtitle: "" };
    case "section_scores":
      return { title: "Section Scores", include_sections: "*", show_charts: true };
    case "page_break":
      return {};
    default:
      return {};
  }
}

function _slug(s) {
  return String(s || "block")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "") || "block";
}

/** Ensure Track 2 shape: instance_id, block_type, block_id, order, enabled, config. */
export function normalizeDraftBlock(raw, index = 0) {
  if (!raw || typeof raw !== "object") return null;
  const blockType = String(raw.block_type || raw.block_id || "").trim();
  if (!blockType) return null;
  const order = Number.isFinite(Number(raw.order)) ? Number(raw.order) : index + 1;
  let instanceId = String(raw.instance_id || "").trim();
  if (!instanceId) {
    instanceId = `blk_${_slug(blockType)}_${order}_${index}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;
  }
  const config =
    raw.config && typeof raw.config === "object"
      ? { ...defaultConfigForBlockType(blockType), ...raw.config }
      : defaultConfigForBlockType(blockType);
  return {
    instance_id: instanceId,
    block_type: blockType,
    block_id: blockType,
    order,
    enabled: raw.enabled !== false,
    config,
  };
}

export function normalizeDraftBlocks(blocks) {
  const expanded = migrateLegacyAnalyzedCompanyBlocks(blocks);
  const out = [];
  expanded.forEach((raw, i) => {
    const entry = normalizeDraftBlock(raw, i);
    if (entry) out.push(entry);
  });
  out.sort((a, b) => a.order - b.order);
  return out.map((b, i) => ({ ...b, order: i + 1 }));
}

/** Display title for structure preview / selected row. */
export function blockDisplayTitle(entry, catalogMap = {}) {
  const type = entry?.block_type || entry?.block_id || "";
  const catalogName = catalogMap[type] || type.replace(/_/g, " ");
  const cfgTitle = (entry?.config?.title || "").trim();
  if (cfgTitle) return cfgTitle;
  return catalogName;
}
